import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Heart, Feather, Globe, Compass, Loader2, ArrowRight, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const EMOTIONS_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/9caaevxq_gemini_generated_video_0aef5c05.mp4";

const TABS = [
  { key: "decode", icon: Feather, label: "tabDecode" },
  { key: "nuances", icon: Compass, label: "tabNuances" },
  { key: "word", icon: Globe, label: "tabWord" },
  { key: "atlas", icon: Heart, label: "tabAtlas" },
];

const panelCls = "glass stats-wow rounded-2xl border border-primary/15 p-6 sm:p-8";

const SUGGEST = { fr: "Suggestions", en: "Suggestions", es: "Sugerencias", nl: "Suggesties" };

function Field({ label, children }) {
  return (
    <div>
      <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-1.5">{label}</p>
      <p className="text-foreground/80 leading-relaxed">{children}</p>
    </div>
  );
}

function SearchPanel({ endpoint, tabKey, param, lang, renderResult }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.emotions.${tabKey}.${k}`);
  const presets = t(`landing.emotions.${tabKey}.presets`, { returnObjects: true }) || [];
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async (value) => {
    const q = (value ?? term).trim();
    if (!q) return;
    setTerm(q);
    setLoading(true);
    try {
      const { data: res } = await api.get(endpoint, { params: { [param]: q, lang }, timeout: 45000 });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("error"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="text-foreground/60 leading-[1.8] text-center max-w-xl mx-auto mb-6">{tr("subtitle")}</p>
      <form
        onSubmit={(e) => { e.preventDefault(); run(); }}
        className="flex flex-col sm:flex-row gap-2.5 max-w-xl mx-auto mb-4"
      >
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={tr("placeholder")}
          data-testid={`emotions-${tabKey}-input`}
          className="flex-1 rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors"
        />
        <button
          type="submit"
          disabled={loading || !term.trim()}
          data-testid={`emotions-${tabKey}-submit`}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {tr("go")}
        </button>
      </form>

      {presets.length > 0 && (
        <div className="mt-2 mb-2 max-w-xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-[0.7rem] uppercase tracking-[0.22em] text-primary/70 mb-3 flex items-center justify-center gap-1.5">
            <Sparkles size={12} /> {SUGGEST[lang] || SUGGEST.fr}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {presets.map((p, i) => (
              <button
                key={i}
                onClick={() => run(p)}
                disabled={loading}
                data-testid={`emotions-${tabKey}-preset-${i}`}
                className="text-xs text-foreground/55 border border-white/10 rounded-full px-3 py-1.5 hover:text-primary hover:border-primary/30 transition-colors disabled:opacity-50"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr("loading")}
        </p>
      )}

      <AnimatePresence mode="wait">
        {!loading && data && (
          <motion.div
            key={term}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="mt-6 space-y-5 text-left max-w-xl mx-auto"
            data-testid={`emotions-${tabKey}-result`}
          >
            {renderResult(data, tr)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DecodePanel({ lang }) {
  return (
    <SearchPanel
      endpoint="/emotions/decode"
      tabKey="decode"
      param="term"
      lang={lang}
      renderResult={(d, tr) => (
        <>
          <p className="font-display text-gradient-orange text-2xl sm:text-3xl text-center mb-2">{d.term}</p>
          {d.origin && <Field label={tr("originLabel")}>{d.origin}</Field>}
          {d.meaning && <Field label={tr("meaningLabel")}>{d.meaning}</Field>}
          {d.nuance && <Field label={tr("nuanceLabel")}>{d.nuance}</Field>}
          {d.wonder && <Field label={tr("wonderLabel")}>{d.wonder}</Field>}
        </>
      )}
    />
  );
}

function AtlasPanel({ lang }) {
  return (
    <SearchPanel
      endpoint="/emotions/atlas"
      tabKey="atlas"
      param="emotion"
      lang={lang}
      renderResult={(d, tr) => (
        <>
          <p className="font-display text-gradient-orange text-2xl sm:text-3xl text-center mb-2">{d.emotion}</p>
          {d.origin && <Field label={tr("originLabel")}>{d.origin}</Field>}
          {d.purpose && <Field label={tr("purposeLabel")}>{d.purpose}</Field>}
          {d.wonder && <Field label={tr("wonderLabel")}>{d.wonder}</Field>}
        </>
      )}
    />
  );
}

function WordPanel({ lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.emotions.word.${k}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/emotions/word-of-day", { params: { refresh, lang }, timeout: 45000 });
      setData(res);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [lang]); // eslint-disable-line

  return (
    <div className="text-center" data-testid="emotions-word">
      {loading && !data ? (
        <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr("loading")}
        </p>
      ) : data ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={data.term}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-xl mx-auto space-y-5 text-left"
          >
            <div className="text-center">
              <p className="font-display text-gradient-orange text-3xl sm:text-4xl">{data.term}</p>
              {data.language && <p className="text-xs uppercase tracking-[0.2em] text-primary/70 mt-1">{tr("langLabel")} · {data.language}</p>}
            </div>
            {data.meaning && <p className="text-foreground/75 leading-[1.8] text-center">{data.meaning}</p>}
            {data.story && <Field label={tr("storyLabel")}>{data.story}</Field>}
          </motion.div>
        </AnimatePresence>
      ) : (
        <p className="font-display italic text-foreground/40 py-7">{tr("error")}</p>
      )}

      <div className="mt-7">
        <button
          onClick={() => load(true)}
          disabled={loading}
          data-testid="emotions-word-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {tr("refresh")}
        </button>
      </div>
    </div>
  );
}

function NuancesPanel({ lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.emotions.nuances.${k}`);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);

  const load = async () => {
    setLoading(true);
    setPicked(null);
    setRound(null);
    try {
      const { data } = await api.get("/emotions/nuances", { params: { lang }, timeout: 45000 });
      setRound(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [lang]); // eslint-disable-line

  const answered = picked !== null;

  return (
    <div className="text-center" data-testid="emotions-nuances">
      <p className="text-foreground/60 leading-[1.8] max-w-xl mx-auto mb-6">{tr("subtitle")}</p>

      {loading ? (
        <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr("loading")}
        </p>
      ) : round ? (
        <div className="max-w-xl mx-auto">
          {round.definition && <p className="font-display italic text-lg sm:text-xl text-foreground/85 mb-2">{round.definition}</p>}
          <p className="text-sm text-foreground/50 mb-6">{tr("question")}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {round.choices.map((c, i) => {
              const isAnswer = c === round.answer;
              const isPicked = c === picked;
              let cls = "text-foreground/70 border-white/12 hover:border-primary/30 hover:text-primary";
              if (answered && isAnswer) cls = "border-emerald-400/50 bg-emerald-400/10 text-emerald-300";
              else if (answered && isPicked) cls = "border-red-400/50 bg-red-400/10 text-red-300";
              else if (answered) cls = "text-foreground/40 border-white/8";
              return (
                <button
                  key={i}
                  disabled={answered}
                  onClick={() => setPicked(c)}
                  data-testid={`emotions-nuances-choice-${i}`}
                  className={`rounded-full border px-5 py-3 text-sm font-medium transition-colors disabled:cursor-default ${cls}`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          {answered && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`mt-6 font-display italic ${picked === round.answer ? "text-emerald-300" : "text-red-300"}`}
              data-testid="emotions-nuances-verdict"
            >
              {picked === round.answer ? tr("correct") : tr("wrong")}
            </motion.p>
          )}

          <div className="mt-6">
            <button
              onClick={load}
              data-testid="emotions-nuances-new"
              className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors"
            >
              <RefreshCw size={13} /> {answered ? tr("again") : tr("newRound")}
            </button>
          </div>
        </div>
      ) : (
        <p className="font-display italic text-foreground/40 py-7">{tr("error")}</p>
      )}
    </div>
  );
}

export function EmotionsLexique({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [active, setActive] = useState(0);

  return (
    <section className="relative overflow-hidden" data-testid="emotions-lexique-section">
      <div className={`max-w-4xl mx-auto px-6 ${embedded ? "pt-2 pb-8" : "py-16 sm:py-20"}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-10"
        >
          {!embedded && (
            <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
              <Sparkles size={14} /> {t("landing.emotions.eyebrow")}
            </p>
          )}
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight mb-4" data-testid="emotions-lexique-title">
            {t("landing.emotions.title")}
          </h2>
          <p className="text-xs text-foreground/40 italic max-w-md mx-auto">{t("landing.emotions.disclaimer")}</p>
        </motion.div>

        {/* cinématique en fondu, sans son */}
        <div className="mb-10 max-w-3xl mx-auto">
          <CinematicVideo fade noSound src={EMOTIONS_VIDEO} testId="emotions-cinematic-video" caption={t("landing.emotions.title")} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-8" role="tablist">
          {TABS.map((tab, i) => {
            const Icon = tab.icon;
            const on = active === i;
            return (
              <button
                key={tab.key}
                onClick={() => setActive(i)}
                data-testid={`emotions-tab-${tab.key}`}
                role="tab"
                aria-selected={on}
                className={`group card-sheen relative flex flex-col items-center justify-center gap-2.5 h-24 sm:h-28 rounded-2xl border px-2.5 text-center overflow-hidden transition-colors duration-300 ${
                  on
                    ? "border-primary/50 bg-primary/[0.08] text-primary shadow-[0_12px_44px_-14px_hsl(28_96%_56%_/_0.6)]"
                    : "border-white/10 bg-white/[0.02] text-foreground/60 hover:border-primary/30 hover:text-primary/90"
                }`}
              >
                {on && (
                  <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 28%, hsl(28 96% 56% / 0.18), transparent 68%)" }} />
                )}
                <span className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-300 ${on ? "border-primary/50 bg-primary/15" : "border-white/10 bg-white/[0.03] group-hover:border-primary/30"}`}>
                  <Icon size={18} strokeWidth={1.7} className={on ? "pulse-star" : ""} />
                </span>
                <span className="relative z-10 text-[11px] sm:text-xs font-medium leading-tight line-clamp-2">{t(`landing.emotions.${tab.label}`)}</span>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className={panelCls}
            data-testid="emotions-panel"
          >
            {active === 0 && <DecodePanel lang={lang} />}
            {active === 1 && <NuancesPanel lang={lang} />}
            {active === 2 && <WordPanel lang={lang} />}
            {active === 3 && <AtlasPanel lang={lang} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
